import React, { useState, useRef } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  alpha,
  Paper,
  Popper,
  Fade,
  ClickAwayListener,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Inventory2Outlined,
  ListAltOutlined,
  AddCircleOutline,
  CategoryOutlined,
  PeopleOutlined,
  PersonAddOutlined,
  ReceiptLongOutlined,
  CallMadeOutlined,
  CallReceivedOutlined,
  LocalShippingOutlined,
  LocalShipping,
  ManageAccountsOutlined,
  AssessmentOutlined,
  PersonOffOutlined,
  SwapVertOutlined,
  TrendingUpOutlined,
  InboxOutlined,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../services/authService';
import { PagePermissionKey } from '../constants/pages';

// Menu configuration with permission keys
interface MenuItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  permissionKey?: PagePermissionKey; // Permission required to see this item
  matchPath?: (pathname: string, search: string) => boolean;
}

interface MenuGroup {
  id: string;
  label: string;
  icon: React.ReactNode;
  basePath: string;
  items: MenuItem[];
}

const menuConfig: MenuGroup[] = [
  {
    id: 'customers',
    label: 'Customer',
    icon: <PeopleOutlined />,
    basePath: '/customers',
    items: [
      { label: 'View All', path: '/customers', icon: <ListAltOutlined />, permissionKey: 'customers_list' },
      { label: 'Add New', path: '/customers/new', icon: <PersonAddOutlined />, permissionKey: 'customers_create' },
      { label: 'View Orders', path: '/customers/orders', icon: <ReceiptLongOutlined />, permissionKey: 'customers_orders' },
      { label: 'View Shipments', path: '/customers/shipments', icon: <LocalShipping />, permissionKey: 'shipments_list' },
      { label: 'Create Outward Order', path: '/customers/orders/outbound/new', icon: <CallMadeOutlined />, permissionKey: 'customers_outbound_create' },
      { label: 'Create Return/Inward Order', path: '/customers/orders/inbound/new', icon: <CallReceivedOutlined />, permissionKey: 'customers_inbound_create' },
    ],
  },
  {
    id: 'suppliers',
    label: 'Supplier',
    icon: <LocalShippingOutlined />,
    basePath: '/suppliers',
    items: [
      { label: 'View All', path: '/suppliers', icon: <ListAltOutlined />, permissionKey: 'suppliers_list' },
      { label: 'Add New', path: '/suppliers/new', icon: <PersonAddOutlined />, permissionKey: 'suppliers_create' },
      { label: 'View Orders', path: '/suppliers/orders', icon: <ReceiptLongOutlined />, permissionKey: 'suppliers_orders' },
      { label: 'Create Inward Order', path: '/suppliers/orders/inbound/new', icon: <CallReceivedOutlined />, permissionKey: 'suppliers_inbound_create' },
    ],
  },
  {
    id: 'items',
    label: 'Item',
    icon: <CategoryOutlined />,
    basePath: '/items',
    items: [
      { label: 'View All', path: '/items', icon: <ListAltOutlined />, permissionKey: 'items_list' },
      { label: 'Add New', path: '/items/new', icon: <AddCircleOutline />, permissionKey: 'items_create' },
      { label: 'Stock Overview', path: '/items/stock', icon: <Inventory2Outlined />, permissionKey: 'inventory_list' },
    ],
  },
  {
    id: 'categories',
    label: 'Categories',
    icon: <CategoryOutlined />,
    basePath: '/categories',
    items: [
      { label: 'View All', path: '/categories', icon: <ListAltOutlined />, permissionKey: 'categories_list' },
      { label: 'Add New', path: '/categories/new', icon: <AddCircleOutline />, permissionKey: 'categories_create' },
    ],
  },
  {
    id: 'users',
    label: 'Users',
    icon: <ManageAccountsOutlined />,
    basePath: '/users',
    items: [
      { label: 'View All', path: '/users', icon: <ListAltOutlined />, permissionKey: 'users_list' },
      { label: 'Add New', path: '/users/new', icon: <PersonAddOutlined />, permissionKey: 'users_create' },
    ],
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: <AssessmentOutlined />,
    basePath: '/reports',
    items: [
      { label: 'Lapsed User', path: '/reports/lapsed-users', icon: <PersonOffOutlined />, permissionKey: 'reports_lapsed' },
      { label: 'Inventory Movement', path: '/reports/inventory-movement', icon: <SwapVertOutlined />, permissionKey: 'reports_inventory_movement' },
      { label: 'Inventory Movement V2', path: '/reports/inventory-movement-v2', icon: <SwapVertOutlined />, permissionKey: 'reports_inventory_movement' },
      { label: 'Item Movements', path: '/reports/item-movements', icon: <SwapVertOutlined />, permissionKey: 'reports_inventory_movement' },
      { label: 'Sales Graph', path: '/reports/sales-graph', icon: <TrendingUpOutlined />, permissionKey: 'reports_sales_graph' },
      { label: 'Pending Order Items', path: '/reports/pending-items', icon: <InboxOutlined />, permissionKey: 'reports_pending_order_items' },
    ],
  },
];

// Theme colors
const colors = {
  primary: '#1976d2',
  primaryLight: '#e3f2fd',
  primaryDark: '#1565c0',
  background: '#ffffff',
  backgroundHover: '#f5f5f5',
  text: '#37474f',
  textSecondary: '#78909c',
  border: '#e0e0e0',
  selectedBg: '#e3f2fd',
  selectedBorder: '#1976d2',
};

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const menuRefs = useRef<Record<string, HTMLElement | null>>({});
  const isHoveringMenu = useRef(false);
  const isHoveringPopup = useRef(false);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Get user and permissions
  const isAdmin = authService.isAdmin();
  const permissions = authService.getPermissions();

  // Helper to check if user has permission (admin has full access)
  const hasPermission = (permissionKey?: PagePermissionKey): boolean => {
    if (isAdmin) return true; // Admin has full access
    if (!permissionKey) return true; // No permission required
    return permissions[permissionKey] === true;
  };

  // Filter menu items based on permissions
  const filteredMenuConfig = menuConfig
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => hasPermission(item.permissionKey)),
    }))
    .filter((group) => group.items.length > 0);

  // Check if dashboard is accessible
  const showDashboard = hasPermission('view_dashboard');

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const scheduleClose = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      if (!isHoveringMenu.current && !isHoveringPopup.current) {
        setActiveGroup(null);
        setAnchorEl(null);
      }
    }, 150);
  };

  const handleGroupEnter = (groupId: string, element: HTMLElement) => {
    clearCloseTimeout();
    isHoveringMenu.current = true;
    setActiveGroup(groupId);
    setAnchorEl(element);
  };

  const handleGroupLeave = () => {
    isHoveringMenu.current = false;
    scheduleClose();
  };

  const handlePopupEnter = () => {
    clearCloseTimeout();
    isHoveringPopup.current = true;
  };

  const handlePopupLeave = () => {
    isHoveringPopup.current = false;
    scheduleClose();
  };

  const handleClickAway = () => {
    isHoveringMenu.current = false;
    isHoveringPopup.current = false;
    setActiveGroup(null);
    setAnchorEl(null);
  };

  const isSelected = (item: MenuItem): boolean => {
    if (item.matchPath) {
      return item.matchPath(location.pathname, location.search);
    }
    return location.pathname === item.path;
  };

  const isGroupActive = (group: MenuGroup): boolean => {
    return location.pathname.startsWith(group.basePath);
  };

  const handleItemClick = (path: string) => {
    navigate(path);
    setActiveGroup(null);
    setAnchorEl(null);
  };

  // Common styles
  const baseButtonStyle = {
    mx: 1,
    my: 0.3,
    borderRadius: 2,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      bgcolor: colors.backgroundHover,
    },
  };

  const groupHeaderStyle = {
    ...baseButtonStyle,
    py: 1.2,
    '& .MuiListItemText-primary': {
      fontWeight: 500,
      fontSize: '0.95rem',
    },
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        sx={{
          width: 220,
          height: '100vh',
          bgcolor: colors.background,
          borderRight: `1px solid ${colors.border}`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Navigation Section */}
        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 1 }}>
          {/* Dashboard - Standalone Item (only for roles with access) */}
          {showDashboard && (
            <>
              <List disablePadding>
                <ListItemButton
                  onClick={() => navigate('/')}
                  sx={{
                    ...baseButtonStyle,
                    py: 1.2,
                    ...(location.pathname === '/' || location.pathname === '/dashboard'
                      ? {
                          bgcolor: colors.selectedBg,
                          borderLeft: `3px solid ${colors.primary}`,
                          '& .MuiListItemIcon-root': { color: colors.primary },
                          '& .MuiListItemText-primary': { color: colors.primary, fontWeight: 600 },
                        }
                      : {}),
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: colors.textSecondary }}>
                    <DashboardIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Dashboard"
                    primaryTypographyProps={{
                      fontWeight: 500,
                      fontSize: '0.95rem',
                    }}
                  />
                </ListItemButton>
              </List>

              <Divider sx={{ my: 1.5, mx: 2 }} />
            </>
          )}

          {/* Section Label */}
          <Typography
            variant="overline"
            sx={{
              px: 3,
              py: 1,
              display: 'block',
              color: colors.textSecondary,
              fontSize: '0.7rem',
              fontWeight: 600,
              letterSpacing: '0.1em',
            }}
          >
            Management
          </Typography>

          {/* Menu Groups with Flyout */}
          <List disablePadding>
            {filteredMenuConfig.map((group) => (
              <React.Fragment key={group.id}>
                {/* Group Header */}
                <ListItemButton
                  ref={(el) => { menuRefs.current[group.id] = el; }}
                  onMouseEnter={(e) => handleGroupEnter(group.id, e.currentTarget)}
                  onMouseLeave={handleGroupLeave}
                  onClick={(e) => handleGroupEnter(group.id, e.currentTarget)}
                  sx={{
                    ...groupHeaderStyle,
                    ...(isGroupActive(group) && {
                      borderLeft: `3px solid ${colors.primary}`,
                      '& .MuiListItemIcon-root': { color: colors.primary },
                      '& .MuiListItemText-primary': { color: colors.primary, fontWeight: 600 },
                    }),
                    ...(activeGroup === group.id && {
                      bgcolor: colors.backgroundHover,
                    }),
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: isGroupActive(group) ? colors.primary : colors.textSecondary,
                    }}
                  >
                    {group.icon}
                  </ListItemIcon>
                  <ListItemText primary={group.label} />
                </ListItemButton>

                {/* Flyout Popup */}
                <Popper
                  open={activeGroup === group.id}
                  anchorEl={anchorEl}
                  placement="right-start"
                  transition
                  sx={{ zIndex: 1300 }}
                  modifiers={[
                    {
                      name: 'offset',
                      options: {
                        offset: [0, 0],
                      },
                    },
                  ]}
                >
                  {({ TransitionProps }) => (
                    <Fade {...TransitionProps} timeout={200}>
                      <Paper
                        className="flyout-menu"
                        onMouseEnter={handlePopupEnter}
                        onMouseLeave={handlePopupLeave}
                        elevation={8}
                        sx={{
                          minWidth: 220,
                          py: 1,
                          borderRadius: 2,
                          border: `1px solid ${colors.border}`,
                          bgcolor: colors.background,
                        }}
                      >
                        {/* Flyout Items */}
                        <List disablePadding>
                          {group.items.map((item) => (
                            <ListItemButton
                              key={item.path}
                              onClick={() => handleItemClick(item.path)}
                              sx={{
                                py: 1,
                                px: 2,
                                '&:hover': {
                                  bgcolor: colors.backgroundHover,
                                },
                                ...(isSelected(item) && {
                                  bgcolor: colors.selectedBg,
                                  borderLeft: `3px solid ${colors.primary}`,
                                  '& .MuiListItemIcon-root': { color: colors.primary },
                                  '& .MuiListItemText-primary': { color: colors.primary, fontWeight: 500 },
                                }),
                              }}
                            >
                              <ListItemIcon
                                sx={{
                                  minWidth: 32,
                                  color: isSelected(item) ? colors.primary : colors.textSecondary,
                                }}
                              >
                                {item.icon}
                              </ListItemIcon>
                              <ListItemText
                                primary={item.label}
                                primaryTypographyProps={{
                                  fontSize: '0.875rem',
                                }}
                              />
                            </ListItemButton>
                          ))}
                        </List>
                      </Paper>
                    </Fade>
                  )}
                </Popper>
              </React.Fragment>
            ))}
          </List>
        </Box>

        {/* Footer Section */}
        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${colors.border}`,
            bgcolor: alpha(colors.primary, 0.02),
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: colors.textSecondary,
              display: 'block',
              textAlign: 'center',
            }}
          >
            Warehouse Admin v1.0
          </Typography>
        </Box>
      </Box>
    </ClickAwayListener>
  );
};

export default Sidebar;